import { type ReactNode } from "react";
import { useSelector } from "react-redux";

import { NumberFormatter as NF } from "~/common/utils/NumberFormatter";
import { userIsDistributor } from "~/domains/user/utils/roles";
import { isConfiguratorProduct } from "~/domains/product/types/ProductData";
import { isProductPMA } from "~/domains/product/utils/productDescriptorUtils";
import { useTranslation } from "react-i18next";

import { ReverseParam } from "~/common/components/Parameter";
import { translateWarehouseName } from "~/utils/language/translationUtils";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { IconButton, InputAdornment, styled, TextField } from "~/common/mui";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { type LineItem, PackageEntrySource } from "~/features/cart/cart/models/CartData";
import { selectIsCartAboveThreshold } from "~/features/cart/cart/redux/selectors/cartSelectors";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";

interface Props {
  product: ProductDescriptor;
  lineItem: LineItem;
  openEdit: () => void;
}

export const EditablePackagingCellOld = (props: Props & { isPackagingSectionOpen?: boolean }) => {
  const { product, lineItem } = props;

  if (isConfiguratorProduct(product.base) || isProductPMA(product)) {
    return <StandardPackagingVariant />;
  }

  if (!product.base.quantityVisible) {
    return <DtsVariant />;
  }

  if (lineItem.source === PackageEntrySource.QUOTE || lineItem.source === PackageEntrySource.CONTRACT) {
    return <ViewablePackagingCellOld {...props} />;
  }

  return <DefaultVariant {...props} />;
};

export const ViewablePackagingCellOld = (props: Omit<Props, "openEdit">) => {
  const { product } = props;

  if (!product.base.quantityVisible) {
    return <DtsVariant />;
  }

  return <DefaultViewableVariant {...props.lineItem} />;
};

const StandardPackagingVariant = () => {
  const { t } = useTranslation();

  return <ReverseParam label={t("cartPage.standardPackaging")}> - </ReverseParam>;
};

const DtsVariant = () => {
  const { t } = useTranslation();

  return <div>{t("product.dts")}</div>;
};

const DefaultViewableVariant = ({
  packSize,
  packCode,
  quantity,
  primaryWarehouseName,
}: Pick<LineItem, "packSize" | "packCode" | "quantity" | "primaryWarehouseName">) => {
  const { t } = useTranslation();
  if (!useSelector(selectIsCartAboveThreshold) && packSize === undefined) {
    return <CircleSpinner />;
  }

  let packagingLabel: ReactNode = "-";
  if (packSize !== undefined && packSize > 0) {
    packagingLabel = t("cartPage.table.header.quantity.value", {
      packSize: packSize,
      code: packCode,
      qty: Math.floor(quantity / packSize),
    });
  }

  return <ReverseParam label={packagingLabel}>{translateWarehouseName(t, primaryWarehouseName)}</ReverseParam>;
};

const DefaultVariant = ({
  lineItem,
  product,
  openEdit,
  isPackagingSectionOpen,
}: Props & { isPackagingSectionOpen?: boolean }) => {
  const { t } = useTranslation();
  if (!useSelector(selectIsCartAboveThreshold) && lineItem.packSize === undefined) {
    return <CircleSpinner />;
  }

  let val: string = "-";
  if (lineItem.packSize !== undefined && lineItem.packSize > 0) {
    val = t("cartPage.table.header.quantity.value", {
      packSize: lineItem.packSize,
      code: lineItem.packCode,
      qty: NF.getDefaultFormatter().format(Math.floor(lineItem.quantity / lineItem.packSize)),
    });
  }

  let icon = <EditIcon />;
  if (userIsDistributor()) {
    icon = isPackagingSectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />;
  }

  return (
    <PackagingCellInput
      disabled={product.isLoading}
      value={val}
      helperText={translateWarehouseName(t, lineItem.primaryWarehouseName)}
      onClick={openEdit}
      icon={icon}
    />
  );
};

const FakeTextField = styled(TextField)({
  pointerEvents: "none",
  [`& .MuiInputBase-root`]: {
    paddingRight: "4px",
  },
  [`& .MuiInputAdornment-root`]: {
    pointerEvents: "all",
  },
});

interface PackagingCellInputProps {
  disabled?: boolean;
  value: string;
  helperText?: string;
  onClick: () => void;
  icon: ReactNode;
}

export const PackagingCellInput = ({ onClick, icon, ...rest }: PackagingCellInputProps) => {
  const { t } = useTranslation();
  return (
    <FakeTextField
      data-test-id={"btn-edit-packaging"}
      label={t(`cartPage.table.content.choosePackaging`)}
      {...rest}
      sx={{ paddingRight: 0 }}
      slotProps={{
        inputLabel: {
          shrink: true,
        },
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={onClick} disabled={rest.disabled} color={"primary"}>
                {icon}
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
};
